import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  login,
  googleLogin,
  resendVerificationEmail,
  requestPasswordReset,
} from "../services/api";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [verifyEmailMode, setVerifyEmailMode] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isGoogleAuthAvailable, setIsGoogleAuthAvailable] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // Vérifie les paramètres d'URL pour les erreurs, le callback de Google, ou la vérification d'email
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const error = params.get("error");
    const verified = params.get("verified");
    // Nouveau paramètre pour la redirection depuis la vérification d'email
    const verification = params.get("verification");

    // Gestion du nouveau paramètre verification
    if (verification) {
      switch (verification) {
        case "success":
          toast.success(
            "Votre email a été vérifié avec succès. Vous pouvez maintenant vous connecter."
          );
          break;
        case "failed":
          toast.error(
            "Le lien de vérification est invalide ou a expiré. Veuillez demander un nouveau lien."
          );
          // Activer automatiquement le mode de vérification d'email en cas d'échec
          setVerifyEmailMode(true);
          break;
        case "error":
          toast.error(
            "Une erreur est survenue lors de la vérification. Veuillez réessayer plus tard."
          );
          break;
        default:
          break;
      }
    }

    // Si email vérifié avec succès (ancien paramètre)
    if (verified === "true") {
      toast.success(
        "Votre email a été vérifié avec succès. Vous pouvez maintenant vous connecter."
      );
    }

    // Si nous avons un token dans l'URL (après redirection de Google)
    if (token) {
      // Stocke le token
      localStorage.setItem("token", token);

      // Décode le token pour obtenir les informations utilisateur
      try {
        const payload = JSON.parse(
          atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
        );

        // Stocke le statut d'administrateur
        localStorage.setItem("isAdmin", payload.isAdmin);

        // Récupérer les données supplémentaires de l'URL si présentes
        const firstName = params.get("firstName");
        const lastName = params.get("lastName");
        const profilePicture = params.get("profilePicture");

        // Stocke les informations additionnelles si présentes
        if (firstName && lastName) {
          localStorage.setItem("userName", `${firstName} ${lastName}`);
        }

        if (profilePicture) {
          localStorage.setItem("profilePicture", profilePicture);
        }

        toast.success("Connexion réussie avec Google !");

        // Redirection basée sur le rôle
        if (payload.isAdmin) {
          navigate("/admin");
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error("Erreur lors du décodage du token:", error);
        toast.error("Erreur lors de la connexion. Veuillez réessayer.");
      }
    }

    // Gestion des erreurs
    if (error === "auth_failed") {
      toast.error("La connexion a échoué. Veuillez réessayer.");
    } else if (error === "google_auth_failed") {
      toast.error(
        "La connexion avec Google a échoué. Veuillez réessayer ou utiliser une autre méthode."
      );
    } else if (error === "google_auth_not_configured") {
      toast.error(
        "L'authentification Google n'est pas disponible actuellement. Veuillez utiliser l'authentification par email."
      );
      // Désactiver le bouton Google
      setIsGoogleAuthAvailable(false);
    }
  }, [location, navigate]);

  // Fonction pour se connecter avec Google
  const handleGoogleSignIn = () => {
    setIsGoogleLoading(true);
    googleLogin();
  };

  // Gestionnaire de soumission du formulaire de connexion
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await login(email, password);

      // Enregistrer les informations d'authentification
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("isAdmin", response.data.user.isAdmin);
      localStorage.setItem(
        "userName",
        `${response.data.user.firstName} ${response.data.user.lastName}`
      );

      if (response.data.user.profilePicture) {
        localStorage.setItem(
          "profilePicture",
          response.data.user.profilePicture
        );
      }

      toast.success("Connexion réussie!");

      // Redirection selon le rôle utilisateur
      if (response.data.user.isAdmin) {
        navigate("/admin");
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Erreur de connexion:", error);

      // Gérer spécifiquement l'erreur d'email non vérifié
      if (
        error.response?.status === 403 &&
        error.response?.data?.message?.includes("vérifier votre email")
      ) {
        toast.error("Veuillez vérifier votre email avant de vous connecter.");
        setVerificationEmail(email);
        setVerifyEmailMode(true);
      } else {
        const errorMessage =
          error.response?.data?.message ||
          "Erreur de connexion. Vérifiez vos identifiants.";
        toast.error(errorMessage);
      }

      setIsLoading(false);
    }
  };

  // Gestionnaire pour le mot de passe oublié
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Veuillez saisir votre adresse email");
      return;
    }

    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setResetEmailSent(true);
      toast.success(
        "Un email de réinitialisation a été envoyé à votre adresse"
      );
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      const errorMessage =
        error.response?.data?.message ||
        "Erreur lors de la demande de réinitialisation. Veuillez réessayer.";
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  // Gestionnaire pour renvoyer l'email de vérification
  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!verificationEmail) {
      toast.error("Veuillez saisir votre adresse email");
      return;
    }

    setIsResendingVerification(true);

    try {
      await resendVerificationEmail(verificationEmail);
      setVerificationEmailSent(true);
      toast.success(
        "Un nouvel email de vérification a été envoyé à votre adresse"
      );
      setIsResendingVerification(false);
    } catch (error) {
      console.error(error);
      const errorMessage =
        error.response?.data?.message ||
        "Erreur lors de l'envoi de l'email de vérification. Veuillez réessayer.";
      toast.error(errorMessage);
      setIsResendingVerification(false);
    }
  };

  // Toggle pour afficher/masquer le mot de passe
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Toggle pour le mode mot de passe oublié
  const toggleForgotPassword = () => {
    setForgotPasswordMode(!forgotPasswordMode);
    setVerifyEmailMode(false);
    setResetEmailSent(false);
  };

  // Toggle pour le mode vérification d'email
  const toggleVerifyEmail = () => {
    setVerifyEmailMode(!verifyEmailMode);
    setForgotPasswordMode(false);
    setVerificationEmailSent(false);
  };

  // Affichage pour le mode "vérification d'email"
  if (verifyEmailMode) {
    return (
      <div className="container d-flex align-items-center justify-content-center min-vh-100">
        <div
          className="card shadow-lg border-0 rounded-3"
          style={{ maxWidth: "500px", width: "100%" }}
        >
          <div className="card-body p-4 p-lg-5">
            <div className="text-center mb-4">
              <i className="bi bi-envelope-check-fill fs-1 text-primary"></i>
              <h2 className="card-title mt-3">Vérification de l'email</h2>
            </div>

            {verificationEmailSent ? (
              <div className="text-center mb-4">
                <div className="alert alert-success bg-success-subtle border-success">
                  <i className="bi bi-envelope-check fs-4 d-block mb-2"></i>
                  <strong>Email envoyé avec succès!</strong>
                </div>
                <p className="text-muted">
                  Veuillez vérifier votre boîte de réception et cliquer sur le
                  lien pour vérifier votre adresse email.
                </p>
                <button
                  className="btn btn-outline-primary mt-3 px-4 py-2"
                  onClick={toggleVerifyEmail}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour à la connexion
                </button>
              </div>
            ) : (
              <form onSubmit={handleResendVerification}>
                <p className="text-muted mb-4">
                  Votre compte doit être vérifié avant de pouvoir vous
                  connecter. Entrez votre adresse email pour recevoir un nouveau
                  lien de vérification.
                </p>

                <div className="mb-4">
                  <label
                    htmlFor="verificationEmail"
                    className="form-label fw-semibold"
                  >
                    Adresse email
                  </label>
                  <div className="input-group input-group-lg">
                    <span
                      className="input-group-text bg-light"
                      id="emailVerificationIcon"
                    >
                      <i className="bi bi-envelope-fill text-primary"></i>
                    </span>
                    <input
                      type="email"
                      className="form-control form-control-lg"
                      id="verificationEmail"
                      placeholder="Entrez votre email"
                      aria-label="Email"
                      value={verificationEmail}
                      onChange={(e) => setVerificationEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100 fw-bold d-flex align-items-center justify-content-center mb-3"
                  disabled={isResendingVerification}
                >
                  {isResendingVerification ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send-fill me-2"></i>
                      Envoyer le lien de vérification
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={toggleVerifyEmail}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour à la connexion
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Affichage pour le mode "mot de passe oublié"
  if (forgotPasswordMode) {
    return (
      <div className="container d-flex align-items-center justify-content-center min-vh-100">
        <div
          className="card shadow-lg border-0 rounded-3"
          style={{ maxWidth: "500px", width: "100%" }}
        >
          <div className="card-body p-4 p-lg-5">
            <div className="text-center mb-4">
              <i className="bi bi-key-fill fs-1 text-primary"></i>
              <h2 className="card-title mt-3">
                Réinitialisation du mot de passe
              </h2>
            </div>

            {resetEmailSent ? (
              <div className="text-center mb-4">
                <div className="alert alert-success bg-success-subtle border-success">
                  <i className="bi bi-envelope-check fs-4 d-block mb-2"></i>
                  <strong>Email envoyé avec succès!</strong>
                </div>
                <p className="text-muted">
                  Veuillez vérifier votre boîte de réception et suivre les
                  instructions pour réinitialiser votre mot de passe.
                </p>
                <button
                  className="btn btn-outline-primary mt-3 px-4 py-2"
                  onClick={toggleForgotPassword}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour à la connexion
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <p className="text-muted mb-4">
                  Entrez votre adresse email pour recevoir un lien de
                  réinitialisation de mot de passe.
                </p>

                <div className="mb-4">
                  <label
                    htmlFor="resetEmail"
                    className="form-label fw-semibold"
                  >
                    Adresse email
                  </label>
                  <div className="input-group input-group-lg">
                    <span
                      className="input-group-text bg-light"
                      id="emailResetIcon"
                    >
                      <i className="bi bi-envelope-fill text-primary"></i>
                    </span>
                    <input
                      type="email"
                      className="form-control form-control-lg"
                      id="resetEmail"
                      placeholder="Entrez votre email"
                      aria-label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100 fw-bold d-flex align-items-center justify-content-center mb-3"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Traitement en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send-fill me-2"></i>
                      Envoyer le lien
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={toggleForgotPassword}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour à la connexion
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Affichage normal du formulaire de connexion
  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100">
      {/* Carte pour la zone de connexion */}
      <div
        className="card shadow-lg border-0 rounded-3"
        style={{ maxWidth: "500px", width: "100%" }}
      >
        <div className="card-body p-4 p-lg-5">
          <div className="text-center mb-4">
            <i className="bi bi-person-circle fs-1 text-primary"></i>
            <h2 className="card-title mt-3">Connexion</h2>
            <p className="text-muted">
              Bienvenue ! Connectez-vous pour accéder à votre compte
            </p>
          </div>

          {/* Bouton de connexion Google */}
          {isGoogleAuthAvailable && (
            <div className="d-grid mb-4">
              <button
                type="button"
                className="btn btn-light position-relative border d-flex align-items-center justify-content-center gap-3 py-3 shadow-sm hover-shadow"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
                style={{ transition: "all 0.3s ease" }}
              >
                {isGoogleLoading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                    <path d="M1 1h22v22H1z" fill="none" />
                  </svg>
                )}
                <span className="fw-medium fs-5">
                  {isGoogleLoading
                    ? "Connexion en cours..."
                    : "Se connecter avec Google"}
                </span>
              </button>
            </div>
          )}

          {/* Section du divider - adaptée en fonction de si Google est disponible */}
          {isGoogleAuthAvailable ? (
            <div className="text-center mb-4">
              <div className="divider d-flex align-items-center justify-content-center">
                <hr className="flex-grow-1" />
                <span className="text-muted mx-3">ou continuez avec</span>
                <hr className="flex-grow-1" />
              </div>
            </div>
          ) : (
            <div className="text-center mb-4">
              <p className="text-muted">
                Connectez-vous avec votre email et mot de passe
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="mb-4">
              <label htmlFor="email" className="form-label fw-semibold">
                Adresse email
              </label>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-light" id="emailIcon">
                  <i className="bi bi-envelope-fill text-primary"></i>
                </span>
                <input
                  type="email"
                  className="form-control form-control-lg"
                  id="email"
                  placeholder="Entrez votre email"
                  aria-label="Email"
                  aria-describedby="emailIcon"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <label htmlFor="password" className="form-label fw-semibold">
                  Mot de passe
                </label>
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0 text-primary small"
                  onClick={toggleForgotPassword}
                >
                  Mot de passe oublié?
                </button>
              </div>
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-light" id="passwordIcon">
                  <i className="bi bi-lock-fill text-primary"></i>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control form-control-lg"
                  id="password"
                  placeholder="Entrez votre mot de passe"
                  aria-label="Mot de passe"
                  aria-describedby="passwordIcon"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  className="input-group-text bg-light"
                  type="button"
                  onClick={togglePasswordVisibility}
                  aria-label={
                    showPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                >
                  <i
                    className={`bi ${
                      showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                    } text-secondary`}
                  ></i>
                </button>
              </div>
            </div>

            {/* Lien pour renvoyer l'email de vérification */}
            <div className="mb-4 text-center">
              <button
                type="button"
                className="btn btn-link text-decoration-none p-0 text-primary small"
                onClick={toggleVerifyEmail}
              >
                Besoin de vérifier votre email?
              </button>
            </div>

            {/* Option "Se souvenir de moi" */}
            <div className="mb-4 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="rememberMe"
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Se souvenir de moi
              </label>
            </div>

            {/* Bouton de connexion avec spinner conditionnel */}
            <button
              type="submit"
              className="btn btn-primary btn-lg w-100 fw-bold d-flex align-items-center justify-content-center py-3 mb-4"
              disabled={isLoading}
              style={{ transition: "all 0.3s ease" }}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Chargement...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  Se connecter
                </>
              )}
            </button>

            <div className="text-center">
              <p className="mb-0">
                Vous n'avez pas de compte ?{" "}
                <Link
                  to="/register"
                  className="text-decoration-none fw-semibold"
                >
                  Inscrivez-vous
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
